import { useAppSelector } from '../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import { useDispatch } from 'react-redux'
import IdeyaTypography from '../../../common/antd/ideya-typography.tsx'
import {
  selectManagementInput,
  setAccountCategory,
  setAccountFirm,
  setAccountFirmRole,
  setAccountGroupRole,
  setAccountInternalRole,
  setAccountIsTrial,
  setAccountStatus,
  setAccountType,
} from '../../../redux/slices/management-input.slice.ts'
import { IdeyaButton } from '../../../common/antd/ideya-button/ideya-button.tsx'
import { convertPxToNumber } from '../../../utils/helpers.ts'
import {
  AccountFirmRole,
  AccountGroupRole,
  AccountInternalRole,
  AccountStatus,
  AccountType,
} from '../../../enums/management-input.enum.ts'
import { VerticalSeparator } from '../../../common/components/vertical-separator.tsx'
import { useIsMobile } from '../../../common/hooks/use-is-mobile.ts'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import { IdeyaSegmented } from '../../../common/antd/ideya-segmented.tsx'
import Select from 'antd/es/select/index'
import Switch from 'antd/es/switch/index'
import Input from 'antd/es/input'
import { useListFirmOptionsQuery } from '../../../apis/option/option-api.api.ts'
import Spin from 'antd/es/spin/index'
import { useState } from 'react'
import { getCategoryOptions } from '../../../utils/selector-options.ts'
import { useTranslation } from 'react-i18next'
import { useIsSuperViewer } from '../../../common/hooks/use-is-superviewer.ts'

interface UserManagementUserControlsProps {
  onCreateUser: () => void
  searchFullName?: string
  setSearchFullName?: (value: string) => void
}

export const UserManagementUserControls: React.FC<
  UserManagementUserControlsProps
> = ({ onCreateUser, searchFullName, setSearchFullName }) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const dispatch = useDispatch()
  const isMobile = useIsMobile(768)
  const { currentData: firmOptionData, isLoading: isLoadingFirmOptions } =
    useListFirmOptionsQuery()
  const [type, setType] = useState<AccountType>(AccountType.External)
  const managementInput = useAppSelector(selectManagementInput)
  const isSuperViewer = useIsSuperViewer()

  return (
    <>
      <Row
        gutter={[0, convertPxToNumber(themeStyle.marginXS)]}
        align={'bottom'}
        justify={isMobile ? 'start' : 'space-between'}
        style={{
          marginBottom: themeStyle.marginSM,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Col flex={isMobile ? 'unset' : 1}>
          {isMobile ? (
            <Row gutter={[12, 12]}>
              <Col xs={24}>
                <Row>
                  <IdeyaSegmented
                    value={managementInput.accountType}
                    onChange={(value) => {
                      setType(value as AccountType)
                      dispatch(setAccountType(value))
                    }}
                    options={[
                      {
                        value: AccountType.External,
                        label: t('user_management.user_controls.external'),
                      },
                      {
                        value: AccountType.Internal,
                        label: t('user_management.user_controls.internal'),
                      },
                    ]}
                  />
                </Row>
              </Col>
              <Col xs={24}>
                <Row>
                  {isLoadingFirmOptions ? (
                    <Spin />
                  ) : (
                    <Select
                      showSearch
                      value={managementInput.accountFirm}
                      style={{ width: '100%' }}
                      onChange={(value) => dispatch(setAccountFirm(value))}
                      options={[
                        {
                          value: '',
                          label: isMobile
                            ? t('common.user_controls.all_firms')
                            : t('common.user_controls.all'),
                        },
                        ...(firmOptionData?.data.map((firm) => ({
                          value: firm.value,
                          label: firm.label,
                        })) || []),
                      ]}
                      filterOption={(input, option) =>
                        (option?.label as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  )}
                </Row>
              </Col>
              <Col xs={24}>
                <Row>
                  <Select
                    showSearch
                    value={managementInput.accountFirmRole}
                    style={{ width: '100%' }}
                    onChange={(value) => dispatch(setAccountFirmRole(value))}
                    options={[
                      {
                        value: AccountFirmRole.All,
                        label: t('common.user_controls.all_firm_roles'),
                      },
                      {
                        value: AccountFirmRole.Compliance,
                        label: 'Compliance',
                      },
                      { value: AccountFirmRole.Normal, label: 'Normal' },
                    ]}
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Row>
              </Col>
              <Col xs={24}>
                <Row>
                  <Select
                    showSearch
                    value={managementInput.accountCategory}
                    style={{ width: '100%' }}
                    onChange={(value) => dispatch(setAccountCategory(value))}
                    options={getCategoryOptions(t)}
                  />
                </Row>
              </Col>
              <Col xs={24}>
                <Row>
                  <Select
                    showSearch
                    value={managementInput.accountGroupRole}
                    style={{ width: '100%' }}
                    onChange={(value) => dispatch(setAccountGroupRole(value))}
                    options={[
                      {
                        value: AccountGroupRole.All,
                        label: isMobile
                          ? t('common.user_controls.all_group_roles')
                          : t('common.user_controls.all'),
                      },
                      { value: AccountGroupRole.Captain, label: 'Captain' },
                      { value: AccountGroupRole.Manager, label: 'Manager' },
                      {
                        value: AccountGroupRole.Contributor,
                        label: 'Contributor',
                      },
                    ]}
                  />
                </Row>
              </Col>
              <Col xs={24}>
                <Row>
                  <Select
                    showSearch
                    value={managementInput.accountStatus}
                    style={{ width: '100%' }}
                    onChange={(value) => dispatch(setAccountStatus(value))}
                    options={[
                      {
                        value: AccountStatus.All,
                        label: isMobile
                          ? t('common.user_controls.all_statuses')
                          : t('common.user_controls.all'),
                      },
                      { value: AccountStatus.Active, label: 'Active' },
                      { value: AccountStatus.Inactive, label: 'Inactive' },
                      {
                        value: AccountStatus.PendingConfirm,
                        label: 'Pending Confirm',
                      },
                    ]}
                  />
                </Row>
              </Col>
              <Col xs={24}>
                <Row>
                  <Input
                    placeholder={t('common.user_controls.search_name')}
                    value={searchFullName}
                    onChange={(e) =>
                      setSearchFullName
                        ? setSearchFullName(e.target.value)
                        : undefined
                    }
                    allowClear
                  />
                </Row>
              </Col>
              <Col xs={16}>
                <Row align={'bottom'}>
                  <Col>
                    <Switch
                      checked={managementInput.accountIsTrial}
                      style={{ marginRight: '4px' }}
                      onChange={(value) => dispatch(setAccountIsTrial(value))}
                    />
                  </Col>
                  <Col>
                    <IdeyaTypography.Text>
                      {t(
                        'user_management.user_controls.only_show_trial_account'
                      )}
                    </IdeyaTypography.Text>
                  </Col>
                </Row>
              </Col>
              <Col
                xs={8}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <IdeyaButton
                  onClick={onCreateUser}
                  style={isMobile ? { width: isMobile ? 80 : 120 } : {}}
                >
                  {t('user_management.user_controls.add_user')}
                </IdeyaButton>
              </Col>
            </Row>
          ) : (
            // Desktop: keep vertical separators
            <Row
              gutter={[
                convertPxToNumber(themeStyle.marginBase),
                convertPxToNumber(themeStyle.marginBase),
              ]}
              style={{
                flexWrap: 'nowrap',
              }}
            >
              <Col>
                <Row>
                  <IdeyaSegmented
                    value={managementInput.accountType}
                    onChange={(value) => {
                      dispatch(setAccountType(value))
                      setType(value as AccountType)
                    }}
                    options={[
                      {
                        value: AccountType.External,
                        label: t('user_management.user_controls.external'),
                      },
                      {
                        value: AccountType.Internal,
                        label: t('user_management.user_controls.internal'),
                      },
                    ]}
                  />
                </Row>
              </Col>
              <Col>
                <VerticalSeparator />
              </Col>
              <Col>
                <Row>
                  {isLoadingFirmOptions ? (
                    <Spin />
                  ) : (
                    <Select
                      showSearch
                      value={managementInput.accountFirm}
                      style={{ width: '180px' }}
                      onChange={(value) => dispatch(setAccountFirm(value))}
                      options={[
                        {
                          value: '',
                          label: t('common.user_controls.all_firms'),
                        },
                        ...(firmOptionData?.data.map((firm) => ({
                          value: firm.value,
                          label: firm.label,
                        })) || []),
                      ]}
                      filterOption={(input, option) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  )}
                </Row>
              </Col>
              <Col>
                <Row>
                  <Select
                    showSearch
                    value={managementInput.accountFirmRole}
                    style={{ width: '150px' }}
                    onChange={(value) => dispatch(setAccountFirmRole(value))}
                    options={[
                      {
                        value: AccountFirmRole.All,
                        label: t('common.user_controls.all_firm_roles'),
                      },
                      {
                        value: AccountFirmRole.Compliance,
                        label: 'Compliance',
                      },
                      { value: AccountFirmRole.Normal, label: 'Normal' },
                    ]}
                  />
                </Row>
              </Col>
              <Col>
                <VerticalSeparator />
              </Col>
              <Col>
                <Row>
                  <Select
                    showSearch
                    value={managementInput.accountCategory}
                    style={{ width: '200px' }}
                    onChange={(value) => dispatch(setAccountCategory(value))}
                    options={getCategoryOptions(t)}
                  />
                </Row>
              </Col>
              <Col>
                <Row>
                  <Select
                    showSearch
                    value={managementInput.accountGroupRole}
                    style={{ width: '150px' }}
                    onChange={(value) => dispatch(setAccountGroupRole(value))}
                    options={[
                      {
                        value: AccountGroupRole.All,
                        label: t('common.user_controls.all_group_roles'),
                      },
                      { value: AccountGroupRole.Captain, label: 'Captain' },
                      { value: AccountGroupRole.Manager, label: 'Manager' },
                      {
                        value: AccountGroupRole.Contributor,
                        label: 'Contributor',
                      },
                    ]}
                  />
                </Row>
              </Col>
              {type === AccountType.Internal && (
                <Col>
                  <Row>
                    <Select
                      showSearch
                      value={managementInput.accountInternalRole}
                      style={{ width: '150px' }}
                      onChange={(value) =>
                        dispatch(setAccountInternalRole(value))
                      }
                      options={[
                        {
                          value: AccountInternalRole.All,
                          label: t('common.user_controls.all_internal_roles'),
                        },
                        { value: AccountInternalRole.Admin, label: 'Admin' },
                        {
                          value: AccountInternalRole.Normal,
                          label: 'Normal',
                        },
                        {
                          value: AccountInternalRole.SuperViewer,
                          label: 'SuperViewer',
                        },
                      ]}
                    />
                  </Row>
                </Col>
              )}
              <Col>
                <VerticalSeparator />
              </Col>
              <Col>
                <Row>
                  <Select
                    showSearch
                    value={managementInput.accountStatus}
                    style={{ width: '150px' }}
                    onChange={(value) => dispatch(setAccountStatus(value))}
                    options={[
                      {
                        value: AccountStatus.All,
                        label: t('common.user_controls.all_statuses'),
                      },
                      { value: AccountStatus.Active, label: 'Active' },
                      { value: AccountStatus.Inactive, label: 'Inactive' },
                      {
                        value: AccountStatus.PendingConfirm,
                        label: 'Pending Confirm',
                      },
                    ]}
                  />
                </Row>
              </Col>
              <Col>
                <VerticalSeparator />
              </Col>
              <Col>
                <Row>
                  <Input
                    placeholder={t('common.user_controls.search_name')}
                    value={searchFullName}
                    onChange={(e) =>
                      setSearchFullName
                        ? setSearchFullName(e.target.value)
                        : undefined
                    }
                    allowClear
                  />
                </Row>
              </Col>
            </Row>
          )}
        </Col>
        {!isMobile && !isSuperViewer && (
          <Row>
            <Col>
              <IdeyaButton onClick={onCreateUser} style={{ width: 120 }}>
                {t('user_management.user_controls.add_user')}
              </IdeyaButton>
            </Col>
          </Row>
        )}
      </Row>
      {!isMobile && (
        <Row
          gutter={[
            convertPxToNumber(themeStyle.marginXS),
            convertPxToNumber(themeStyle.marginXS),
          ]}
          style={{
            marginBottom: themeStyle.marginSM,
            flexDirection: 'row',
          }}
        >
          <Col>
            <Switch
              checked={managementInput.accountIsTrial}
              size={'small'}
              onChange={(value) => dispatch(setAccountIsTrial(value))}
            />
          </Col>
          <Col>
            <IdeyaTypography.Text>
              {t('user_management.user_controls.only_show_trial_account')}
            </IdeyaTypography.Text>
          </Col>
        </Row>
      )}
    </>
  )
}
